import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';

import { VoiceService } from '../../../services/voiceService';

/**
 * Whether to show the mic. Off unless `VITE_ENABLE_VOICE_INPUT=true`: the API
 * has no `/api/v3/voice/transcribe` endpoint yet, so every recording failed.
 */
export function isVoiceInputEnabled(): boolean {
  return String(import.meta.env.VITE_ENABLE_VOICE_INPUT ?? '').trim().toLowerCase() === 'true';
}

/**
 * Spike 1 §1.4 — voice capture button.
 *
 * Uses the native `MediaRecorder` API (zero new deps) instead of `react-mic`.
 * The original spike spec called out `react-mic`, but the package is 4 years
 * stale (last release 2022) and depends on the deprecated `react-ga` v3 —
 * adding it would bring deprecated transitive deps into v3.0. Native
 * MediaRecorder offers the same affordances (start/stop, blob output) with
 * no maintenance risk; behavior is identical from the user's POV.
 *
 * Flow:
 *   tap mic  → request mic permission → start recording (audio/webm).
 *   tap stop → finalize blob → POST to /api/v3/voice/transcribe via VoiceService
 *              → resolve with the final transcript.
 *
 * The transcript bubbles up via `onTranscript`; the parent decides where it
 * lands (AgentBar prepends to the input field; Lexical AIInsertPlugin can
 * consume it at the cursor for the canvas-direct-dictation path).
 *
 * Error states:
 *   - mic permission denied → muted icon + tooltip prompt to re-grant.
 *   - browser doesn't support MediaRecorder → button disabled, tooltip explains.
 *   - transcribe network error → revert UI, fire onError (parent toasts).
 */
interface Props {
  onTranscript: (text: string) => void;
  onError?: (error: Error) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

type MicState = 'idle' | 'requesting' | 'recording' | 'transcribing' | 'denied' | 'unsupported';

export const VoiceMicButton: React.FC<Props> = ({ onTranscript, onError, size = 'small', disabled }) => {
  const [state, setState] = useState<MicState>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Probe support once on mount so the button can disable preemptively rather
  // than failing on first click.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
      setState('unsupported');
    }
  }, []);

  // Always release the mic when the component unmounts; recorders without
  // an explicit stop hold onto the OS-level capture and the mic indicator
  // stays lit even after navigation.
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
    };
  }, []);

  const start = async () => {
    if (state === 'recording' || state === 'requesting') return;
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = pickSupportedMime();
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        setState('transcribing');
        try {
          const text = await VoiceService.transcribe(blob);
          if (text.trim().length > 0) onTranscript(text);
        } catch (err) {
          const e = err instanceof Error ? err : new Error('Transcription failed.');
          onError?.(e);
        } finally {
          setState('idle');
          streamRef.current?.getTracks().forEach(t => t.stop());
          streamRef.current = null;
          recorderRef.current = null;
        }
      };
      recorder.start(250); // ~4 chunks/sec — keeps the final blob assembly cheap.
      recorderRef.current = recorder;
      setState('recording');
    } catch (err) {
      // The two common failures: user denied permission (NotAllowedError) or
      // no microphone present (NotFoundError). Both collapse to "denied" in UI.
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      setState('denied');
      onError?.(err instanceof Error ? err : new Error('Microphone unavailable.'));
    }
  };

  const stop = () => {
    if (state !== 'recording') return;
    recorderRef.current?.stop();
  };

  const handleClick = () => {
    if (state === 'recording') stop();
    else start();
  };

  const tooltip = (() => {
    switch (state) {
      case 'recording':   return 'Stop recording';
      case 'requesting':  return 'Requesting mic permission…';
      case 'transcribing':return 'Transcribing…';
      case 'denied':      return 'Microphone access denied — check site permissions';
      case 'unsupported': return 'Voice capture isn\'t supported in this browser';
      default:            return 'Tap to dictate';
    }
  })();

  return (
    <Tooltip title={tooltip}>
      <Box component="span">
        <IconButton
          size={size}
          onClick={handleClick}
          disabled={disabled || state === 'unsupported' || state === 'requesting' || state === 'transcribing'}
          aria-label={state === 'recording' ? 'Stop recording' : 'Start dictation'}
          aria-pressed={state === 'recording'}
          color={state === 'recording' ? 'error' : 'default'}
        >
          {state === 'recording' ? (
            <StopIcon fontSize={size === 'small' ? 'small' : 'medium'} />
          ) : state === 'transcribing' || state === 'requesting' ? (
            <CircularProgress size={size === 'small' ? 16 : 20} />
          ) : state === 'denied' || state === 'unsupported' ? (
            <MicOffIcon fontSize={size === 'small' ? 'small' : 'medium'} />
          ) : (
            <MicIcon fontSize={size === 'small' ? 'small' : 'medium'} />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
};

/**
 * Pick the most compatible MIME type the current browser supports. Chrome /
 * Edge default to `audio/webm;codecs=opus`; Safari prefers `audio/mp4`.
 * Returning null lets MediaRecorder fall back to its own default.
 */
function pickSupportedMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return null;
}
